import React from 'react'
import { Box, ToggleButton, ToggleButtonGroup, Typography, alpha } from '@mui/material'
import type { Theme } from '@mui/material/styles'
import PublicIcon from '@mui/icons-material/Public'
import CampaignIcon from '@mui/icons-material/Campaign'
import HubIcon from '@mui/icons-material/Hub'
import VideoLibraryIcon from '@mui/icons-material/VideoLibrary'
import ChatBubbleOutlineIcon from '@mui/icons-material/ChatBubbleOutline'
import {
  derivePlatformOptions,
  platformChoice,
  ALL_PLATFORMS_CHOICE,
  type PlatformChoice,
  type PlatformSelection,
  type PlatformToggleCounts,
} from './platformFilter'
import { ALL_SOURCES_CHOICE, deriveBusinessSourceOptions } from './businessSource'
import CampaignFilter from './CampaignFilter'
import { updatingProps, updatingSx } from '../socialdash/updatingStyle'
import type { LabelValue } from '../../types/gotypes'

export interface PlatformFilterBarProps {
  /**
   * The platforms this mount HAS, from `/entities/filter-summary` — unfiltered,
   * because this decides which toggles exist and in what order. Its counts are
   * only a fallback for when `counts` has not arrived.
   */
  platforms: LabelValue[]
  /**
   * What each button is worth under the page's CURRENT filters, from
   * `useSkyPlatformCounts`. Omitted while it loads (or on a mount that has no
   * such endpoint), and then no counts are drawn at all rather than zeros.
   *
   * ⚠ THE TOTALS ARE NOT THE SUM OF THE BUTTONS, and the bar says so in words:
   * `totalVideos` also counts videos with no platform recorded, `totalComments`
   * also counts comments whose parent post is not in this campaign. Each number
   * is what pressing its own button gives you, which is the only property a
   * count on a button has to have.
   */
  counts?: PlatformToggleCounts
  /** The counts are the previous filter's while the new ones load: the chips
   *  are dimmed and busy; the buttons stay usable. */
  countsUpdating?: boolean
  /** Campaign counts from the same summary. Omitted while it loads. */
  campaigns?: LabelValue[]
  /** Selected campaign ids; empty means every campaign. */
  selectedCampaigns?: string[]
  onCampaignsChange?: (campaigns: string[]) => void
  selection: PlatformSelection
  /**
   * Choose ONE platform, or ALL_PLATFORMS_CHOICE. Single-select, so there is no
   * separate "all platforms" action any more: All is a button like the others
   * (Kai, 2026-08-14 — "you click on the one you want to see, as opposed to at
   * the moment, [where] switching between them is quite a lot", which took a
   * de-select of three platforms to isolate the fourth).
   */
  onSelectPlatform: (choice: PlatformChoice) => void
  /**
   * The Source row (social, influencer, …): the post's BUSINESS source, an
   * entity filter exactly like Platform. Omit `onSelectSource` and the row is
   * not drawn. Its All button reads the source counts' own totals (every post
   * and comment under the other filters, whatever the source).
   */
  sourceChoice?: string
  onSelectSource?: (choice: string) => void
  /** Posts and comments per source under the page's filters (useSkySourceCounts). */
  sourceCounts?: PlatformToggleCounts
  disabled?: boolean
}

/**
 * Shared styling for the bar's single-choice groups (Platform and Source).
 * Selected reads as info/blue in both themes: a tinted fill whose alpha is
 * raised in dark mode so the state survives the darker surface, with the
 * label and border on `info.main` so the contrast does not rest on the fill.
 */
const EXCLUSIVE_GROUP_SX = {
  gap: 0.5,
  '& .MuiToggleButtonGroup-grouped': { borderRadius: 1.5, border: '1px solid' },
  '& .MuiToggleButton-root': {
    textTransform: 'none',
    fontWeight: 600,
    px: 1.5,
    py: 0.5,
    gap: 0.75,
    borderColor: 'divider',
    color: 'text.secondary',
    '&.Mui-selected': {
      color: 'info.main',
      borderColor: 'info.main',
      bgcolor: (theme: Theme) => alpha(theme.palette.info.main, theme.palette.mode === 'dark' ? 0.24 : 0.1),
      '&:hover': {
        bgcolor: (theme: Theme) => alpha(theme.palette.info.main, theme.palette.mode === 'dark' ? 0.32 : 0.16),
      },
    },
  },
} as const

/**
 * The two numbers a platform button is worth, in the SAME icon language the
 * page header uses for the same two populations — so the chip on the button and
 * the count in the header teach each other rather than each needing a legend.
 *
 * A null count draws nothing at all. See PlatformOption.comments: "we did not
 * ask" and "there are none" must not render identically.
 */
const CountChips: React.FC<{
  testIdSuffix: string
  videos: number | null
  comments: number | null
  updating?: boolean
}> = ({ testIdSuffix, videos, comments, updating = false }) => (
  <Box
    component="span"
    {...updatingProps(updating)}
    sx={{
      display: 'inline-flex',
      alignItems: 'center',
      gap: 0.75,
      ml: 0.25,
      ...updatingSx(updating),
      opacity: updating ? 0.45 : 0.8,
    }}
  >
    {videos !== null && (
      <Box
        component="span"
        data-testid={`autocoder-social-platform-count-${testIdSuffix}`}
        sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.25, fontSize: '0.7rem' }}
      >
        <VideoLibraryIcon sx={{ fontSize: 13 }} />
        {videos.toLocaleString()}
      </Box>
    )}
    {comments !== null && (
      <Box
        component="span"
        data-testid={`autocoder-social-platform-comments-${testIdSuffix}`}
        sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.25, fontSize: '0.7rem' }}
      >
        <ChatBubbleOutlineIcon sx={{ fontSize: 13 }} />
        {comments.toLocaleString()}
      </Box>
    )}
  </Box>
)

/** The accessible-name half of the same two numbers. */
function countsLabel(videos: number | null, comments: number | null): string {
  const parts: string[] = []
  if (videos !== null) parts.push(`${videos} posts`)
  if (comments !== null) parts.push(`${comments} comments`)
  return parts.length > 0 ? `, ${parts.join(', ')}` : ''
}

/**
 * The dashboard's headline controls: which platforms, and by which ingest path
 * the comments arrived.
 *
 * Both are permanently on screen rather than tucked into the sidebar, because
 * both change every number on the page — a default the user cannot see is its
 * own kind of lie. The platform toggles are derived from the job's data, so a
 * platform nobody has written code for still gets a button.
 *
 * THE AUTHOR-SCOPE CONTROL WAS REMOVED (Kai, 2026-08-14: "that's not a filter
 * that makes sense"). Its FILTER went with it — useSocialFilters now forces
 * `all` and ignores the persisted value — because leaving the default at
 * `audience` with no control would have been precisely the invisible default
 * the paragraph above refuses. Removing a control and keeping its effect is
 * strictly worse than either having it or not.
 *
 * ⚠ That widened every chart: brand replies were being excluded and now are
 * not. The numbers legitimately moved.
 */
export const PlatformFilterBar: React.FC<PlatformFilterBarProps> = ({
  platforms,
  counts,
  countsUpdating = false,
  campaigns,
  selectedCampaigns,
  onCampaignsChange,
  selection,
  onSelectPlatform,
  sourceChoice = ALL_SOURCES_CHOICE,
  onSelectSource,
  sourceCounts,
  disabled = false,
}) => {
  const options = derivePlatformOptions(platforms, counts)
  const choice = platformChoice(selection)
  const sourceOptions = deriveBusinessSourceOptions(sourceCounts)
  // The campaign control only exists when the page can act on it: something to
  // choose between AND somewhere to send the choice.
  const showCampaigns = Boolean(onCampaignsChange) && (campaigns?.length ?? 0) > 1
  const showSources = Boolean(onSelectSource)

  // A job with no entities has no platform toggles (its comments are all
  // parentless). With the captions gone there is nothing else this bar can
  // say, so it disappears entirely rather than rendering an empty strip.
  if (options.length === 0 && !showSources && !showCampaigns) return null

  return (
    <Box
      data-testid="autocoder-social-filter-bar"
      sx={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'flex-start',
        gap: 1.5,
        px: 3,
        py: 2,
        bgcolor: 'background.paper',
        borderBottom: '1px solid',
        borderColor: 'divider',
      }}
    >
      <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 2, rowGap: 1.5 }}>
      {/* Campaign — first, because it is the widest cut: it decides WHICH of
          Sky's content the whole page is about, where platform decides where
          that content was published. */}
      {showCampaigns && (
        <CampaignFilter
          campaigns={campaigns ?? []}
          selected={selectedCampaigns ?? []}
          onChange={onCampaignsChange ?? (() => {})}
          disabled={disabled}
        />
      )}

      {/* Platforms */}
      {options.length > 0 && (
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, minWidth: 92 }}>
          <PublicIcon sx={{ fontSize: 16, color: 'text.secondary' }} />
          <Typography
            variant="caption"
            sx={{
              color: 'text.secondary',
              fontWeight: 600,
              textTransform: 'uppercase',
              letterSpacing: '0.06em',
            }}
          >
            Platform
          </Typography>
        </Box>

        <ToggleButtonGroup
          size="small"
          exclusive
          data-testid="autocoder-social-platform-group"
          aria-label="Filter by platform"
          value={choice}
          // `value &&` is what makes clicking the LIT button a no-op rather than
          // a de-select. There is no unselected state to fall back to here —
          // every choice, including All, is one of the buttons.
          onChange={(_e, next: PlatformChoice | null) => next && onSelectPlatform(next)}
          sx={{ ...EXCLUSIVE_GROUP_SX, flexWrap: 'wrap' }}
        >
          <ToggleButton
            value={ALL_PLATFORMS_CHOICE}
            disabled={disabled}
            data-testid="autocoder-social-platform-all"
            aria-label={`All platforms${countsLabel(
              counts?.totalVideos ?? null,
              counts?.totalComments ?? null,
            )}`}
          >
            All
            <CountChips
              testIdSuffix={ALL_PLATFORMS_CHOICE}
              videos={counts?.totalVideos ?? null}
              comments={counts?.totalComments ?? null}
              updating={countsUpdating}
            />
          </ToggleButton>

          {options.map((option) => (
            <ToggleButton
              key={option.value}
              value={option.value}
              disabled={disabled}
              // The testid carries the PLATFORM KEY alone. The accessible name
              // deliberately carries the counts ("TikTok, 2 videos, 40
              // comments"), so a test that locates a toggle by name has to know
              // how much data the job holds — the exact coupling this id
              // removes.
              data-testid={`autocoder-social-platform-${option.value}`}
              aria-label={`${option.label}${countsLabel(option.videos, option.comments)}`}
            >
              {option.label}
              <CountChips
                testIdSuffix={option.value}
                videos={option.videos}
                comments={option.comments}
                updating={countsUpdating}
              />
            </ToggleButton>
          ))}
        </ToggleButtonGroup>
      </Box>
      )}

      </Box>

      {/* SOURCE — the second row, drawn exactly like Platform (Kai, 21 Oct
          2026: "platform and then source as two rows in exactly the same
          style"). Both are filters on the post; how its comments were fetched
          (Sprinklr, Apify) is configuration and has no control. */}
      {showSources && (
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, minWidth: 92 }}>
          <HubIcon sx={{ fontSize: 16, color: 'text.secondary' }} />
          <Typography
            variant="caption"
            sx={{
              color: 'text.secondary',
              fontWeight: 600,
              textTransform: 'uppercase',
              letterSpacing: '0.06em',
            }}
          >
            Source
          </Typography>
        </Box>

        <ToggleButtonGroup
          size="small"
          exclusive
          data-testid="autocoder-social-source-group"
          aria-label="Filter by source"
          value={sourceChoice}
          onChange={(_e, next: string | null) => next && onSelectSource?.(next)}
          sx={{ ...EXCLUSIVE_GROUP_SX, flexWrap: 'wrap' }}
        >
          <ToggleButton
            value={ALL_SOURCES_CHOICE}
            disabled={disabled}
            data-testid="autocoder-social-source-all"
            aria-label={`All sources${countsLabel(
              sourceCounts?.totalVideos ?? null,
              sourceCounts?.totalComments ?? null,
            )}`}
          >
            All
            <CountChips
              testIdSuffix={`source-${ALL_SOURCES_CHOICE}`}
              videos={sourceCounts?.totalVideos ?? null}
              comments={sourceCounts?.totalComments ?? null}
              updating={countsUpdating}
            />
          </ToggleButton>
          {sourceOptions.map((option) => (
            <ToggleButton
              key={option.value}
              value={option.value}
              disabled={disabled}
              data-testid={`autocoder-social-source-${option.value}`}
              aria-label={`${option.label}${countsLabel(option.videos, option.comments)}`}
            >
              {option.label}
              <CountChips
                testIdSuffix={`source-${option.value}`}
                videos={option.videos}
                comments={option.comments}
                updating={countsUpdating}
              />
            </ToggleButton>
          ))}
        </ToggleButtonGroup>
      </Box>
      )}

    </Box>
  )
}

export default PlatformFilterBar
